import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Cpu,
  Activity,
  DollarSign,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { AICostDashboard } from '../components/AICostDashboard';
import { useAICosts } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';

interface ModelHealthEntry {
  id: number;
  provider: string;
  model: string;
  circuitState: string;
  consecutiveFailures: number;
  lastFailureAt: string | null;
  lastSuccessAt: string | null;
  avgLatencyMs: number | null;
  successRate: number | null;
  updatedAt: string;
}

const queryKeys = {
  modelHealth: ['ai-config', 'model-health'] as const,
};

export default function AIModelConfig() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'costs' | 'health'>('costs');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: costData, isLoading: costsLoading } = useAICosts();

  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = useQuery<{ models: ModelHealthEntry[] }>({
    queryKey: queryKeys.modelHealth,
    queryFn: async () => {
      const res = await fetch('/api/ai/model-health');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    refetchInterval: 30000,
    enabled: activeTab === 'health',
  });

  const handleRefreshConfig = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch('/api/ai/refresh-config', { method: 'POST' });
      const data = await res.json();
      showToast(data.message || 'Config refresh requested', 'success');
    } catch {
      showToast('Failed to refresh config', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const circuitStateIcon = (state: string) => {
    switch (state) {
      case 'closed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'open':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'half_open':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-400" />;
    }
  };

  const circuitStateBadge = (state: string) => {
    const styles: Record<string, string> = {
      closed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      open: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      half_open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    };
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${styles[state] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}>
        {circuitStateIcon(state)}
        {state.replace('_', ' ')}
      </span>
    );
  };

  const tabs = [
    { id: 'costs' as const, label: 'Cost Dashboard', icon: DollarSign },
    { id: 'health' as const, label: 'Model Health', icon: Activity },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Cpu className="h-6 w-6 text-purple-500" />
            AI Configuration
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor AI costs, model health, and pipeline configuration
          </p>
        </div>
        <button
          onClick={handleRefreshConfig}
          disabled={isRefreshing}
          className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh Config
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'costs' && (
        <div>
          {costsLoading ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : costData ? (
            <AICostDashboard data={costData} />
          ) : (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-8 text-center">
              <DollarSign className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No cost data available yet</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'health' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Model Health Status</h3>
            <button
              onClick={() => refetchHealth()}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Refresh
            </button>
          </div>

          {healthLoading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : !healthData?.models?.length ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-8 text-center">
              <Activity className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No model health data recorded yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Health data will appear after models are used in the AI pipeline
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Provider</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Model</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Circuit</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Success Rate</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Avg Latency</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Failures</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Last Success</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {healthData.models.map((model) => (
                    <tr key={model.id} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium">{model.provider}</td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-400 font-mono text-xs truncate max-w-[200px]" title={model.model}>
                        {model.model}
                      </td>
                      <td className="px-4 py-2">{circuitStateBadge(model.circuitState)}</td>
                      <td className="px-4 py-2 text-right">
                        {model.successRate != null ? (
                          <span className={`font-medium ${
                            model.successRate >= 0.95 ? 'text-green-600 dark:text-green-400' :
                            model.successRate >= 0.8 ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-red-600 dark:text-red-400'
                          }`}>
                            {(model.successRate * 100).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">
                        {model.avgLatencyMs != null ? `${model.avgLatencyMs}ms` : '-'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={model.consecutiveFailures > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400'}>
                          {model.consecutiveFailures}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs">
                        {model.lastSuccessAt
                          ? new Date(model.lastSuccessAt).toLocaleString()
                          : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
